// client/src/components/common/ConfirmModal.js
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Trash2, Info, X } from 'lucide-react';

const iconMap = {
  warning: { Icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  danger: { Icon: Trash2, color: 'text-red-500', bg: 'bg-red-50' },
  info: { Icon: Info, color: 'text-blue-500', bg: 'bg-blue-50' },
};

const colorMap = {
  red: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  yellow: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
  blue: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  green: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
};

const ConfirmModal = ({
  isOpen,
  title,
  message,
  icon = 'warning',
  confirmLabel,
  cancelLabel,
  confirmColor = 'red',
  onConfirm,
  onCancel,
  children,
}) => {
  const { t } = useTranslation('common');
  const confirmRef = useRef(null);

  const onConfirmRef = useRef(onConfirm);
  const onCancelRef = useRef(onCancel);
  onConfirmRef.current = onConfirm;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!isOpen) return;
    const handle = (e) => {
      if (e.key === 'Escape') onCancelRef.current?.();
      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        onConfirmRef.current?.();
      }
    };
    document.addEventListener('keydown', handle);
    confirmRef.current?.focus();
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen]);

  if (!isOpen) return null;

  const { Icon, color, bg } = iconMap[icon] || iconMap.warning;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-10 h-10 ${bg} rounded-full flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {message && <p className="mt-1 text-sm text-gray-600">{message}</p>}
              {children && <div className="mt-3">{children}</div>}
            </div>
            <button onClick={onCancel} className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            {cancelLabel || t('cancel')}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${colorMap[confirmColor] || colorMap.red}`}
          >
            {confirmLabel || t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
